import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getImageUrl } from '../../api/client';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import AdminLoading from './AdminLoading';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { IoReceiptOutline, IoShirtOutline, IoPricetagsOutline, IoPeopleOutline } from 'react-icons/io5';
import { IoEyeOutline, IoEllipsisVertical, IoCalendarOutline, IoAlertCircleOutline, IoArrowForwardOutline } from 'react-icons/io5';
import { FaOm, FaMoon, FaStar, FaFlag, FaHeart, FaFire } from 'react-icons/fa';
import { getUpcomingFestivals, daysUntil, TYPE_COLORS, PRIORITY_COLORS } from './festivalData';

const FESTIVAL_TYPE_ICONS = {
  hindu:    FaOm,
  muslim:   FaMoon,
  christian: FaStar,
  national: FaFlag,
  season:   FaHeart,
};

const ORDER_STATUS_COLORS = {
  pending_payment: '#ff9800',
  confirmed: '#2196f3',
  out_for_delivery: '#9c27b0',
  delivered: '#4caf50',
  returned: '#9e9e9e',
  cancelled: '#f44336',
};

const ORDER_STATUS_COLORS_CHIP = {
  pending_payment: 'warning',
  confirmed: 'info',
  out_for_delivery: 'primary',
  delivered: 'success',
  returned: 'default',
  cancelled: 'error',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function FestivalCountdownWidget() {
  const upcoming = useMemo(() => getUpcomingFestivals(5), []);
  const next = upcoming[0] || null;

  if (!next) return null;

  const nextDays = daysUntil(next.date);
  const needsAttention = upcoming.filter((f) => daysUntil(f.date) <= f.prepDays);

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 4,
        borderRadius: 2,
        borderColor: needsAttention.length ? '#ff9800' : 'divider',
        bgcolor: needsAttention.length ? '#fffde7' : 'background.paper',
        boxShadow: needsAttention.length ? '0 2px 12px rgba(255,152,0,0.18)' : '0 4px 20px rgba(0,0,0,0.06)',
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'rgba(115,103,240,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IoCalendarOutline size={22} color="#7367F0" />
            </Box>
            <Box>
              <Typography variant="body1" fontWeight={700}>Upcoming Festivals</Typography>
              <Typography variant="caption" color="text.secondary">
                {needsAttention.length > 0
                  ? `${needsAttention.length} festival${needsAttention.length > 1 ? 's' : ''} need${needsAttention.length === 1 ? 's' : ''} slider images now!`
                  : 'Next 5 upcoming — plan your hero images ahead'}
              </Typography>
            </Box>
          </Box>
          <Button
            component="a"
            href="/admin/pages/festival-schedule"
            size="small"
            endIcon={<IoArrowForwardOutline />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Manage all
          </Button>
        </Box>

        {/* Next festival big highlight */}
        {(() => { const NextIcon = FESTIVAL_TYPE_ICONS[next.type] || FaFire; return (
        <Box sx={{
          mb: 2,
          p: 2,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #7367F0 0%, #9E95F5 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 64 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
              <NextIcon size={24} color="#fff" />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1, color: '#fff' }}>
              {nextDays === 0 ? '!' : nextDays}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500, letterSpacing: 1 }}>
              {nextDays === 0 ? 'TODAY' : nextDays === 1 ? 'TOMORROW' : 'DAYS AWAY'}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
              {next.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
              {new Date(next.date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </Typography>
          </Box>
          {nextDays <= next.prepDays && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 1, px: 1.5, py: 0.75 }}>
              <IoAlertCircleOutline size={16} />
              <Typography variant="caption" fontWeight={700}>Upload slider now!</Typography>
            </Box>
          )}
        </Box>
        ); })()}

        {/* Rest of the upcoming list */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 1.5 }}>
          {upcoming.slice(1).map((f, i) => {
            const days = daysUntil(f.date);
            const tc = TYPE_COLORS[f.type] || TYPE_COLORS.hindu;
            const urgent = days <= f.prepDays;
            const FIcon = FESTIVAL_TYPE_ICONS[f.type] || FaFire;
            return (
              <Box key={i} sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: urgent ? '#ff9800' : tc.border,
                bgcolor: urgent ? '#fff8e1' : tc.bg,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <FIcon size={14} color={tc.color} />
                    <Typography variant="body2" fontWeight={700} sx={{ color: tc.color }}>
                      {f.name}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PRIORITY_COLORS[f.priority], flexShrink: 0 }} />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {new Date(f.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                </Typography>
                <Typography variant="caption" fontWeight={600}
                  sx={{ color: days <= 7 ? '#d32f2f' : days <= 30 ? '#f57c00' : 'text.secondary' }}>
                  {days === 0 ? 'Today!' : days === 1 ? 'Tomorrow' : `${days} days`}
                </Typography>
                {urgent && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IoAlertCircleOutline size={12} color="#d32f2f" />
                    <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 600 }}>Prepare slider!</Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(6);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    Promise.all([
      api('/api/admin/orders?per_page=200').catch(() => ({})),
      api('/api/admin/products').catch(() => []),
      api('/api/admin/categories').catch(() => []),
    ])
      .then(([ordersData, productsData, categoriesData]) => {
        // API returns { orders: [...], total, pages } — extract the array
        const ordersArr = Array.isArray(ordersData)
          ? ordersData
          : Array.isArray(ordersData?.orders)
          ? ordersData.orders
          : [];
        setOrders(ordersArr);
        setProducts(Array.isArray(productsData) ? productsData : []);
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const formatPaise = (p) => (p != null ? `₹${(Number(p) / 100).toLocaleString('en-IN')}` : '–');

  const stats = useMemo(() => {
    const revenue = orders.reduce((sum, o) => sum + (Number(o.total_paise) || 0), 0);
    const customers = new Set(orders.map((o) => (o.contact_email || o.contact_name || '').toString().trim()).filter(Boolean)).size;
    return {
      orders: orders.length,
      products: products.length,
      categories: categories.length,
      revenue,
      customers,
    };
  }, [orders, products, categories]);

  const ordersByStatus = useMemo(() => {
    const map = {};
    orders.forEach((o) => {
      const s = o.status || 'pending_payment';
      map[s] = (map[s] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({
      name: name.replace(/_/g, ' '),
      value,
      color: ORDER_STATUS_COLORS[name] || '#9e9e9e',
    }));
  }, [orders]);

  const revenueByMonth = useMemo(() => {
    const map = {};
    MONTHS.forEach((m, i) => { map[i] = { month: m, revenue: 0, orders: 0 }; });
    orders.forEach((o) => {
      const date = o.event_date || o.created_at;
      let monthIdx = 0;
      if (date) {
        const d = new Date(date);
        if (!isNaN(d)) monthIdx = d.getMonth();
      }
      if (!map[monthIdx]) map[monthIdx] = { month: MONTHS[monthIdx], revenue: 0, orders: 0 };
      map[monthIdx].revenue += Number(o.total_paise) || 0;
      map[monthIdx].orders += 1;
    });
    return MONTHS.map((m, i) => map[i] || { month: m, revenue: 0, orders: 0 });
  }, [orders]);

  const filteredOrders = useMemo(() => {
    let list = orders;
    if (statusFilter) {
      list = list.filter((o) => (o.status || '').toLowerCase().replace(/_/g, ' ') === statusFilter.toLowerCase().replace(/_/g, ' '));
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (o) =>
          String(o.id).toLowerCase().includes(q) ||
          (o.contact_name || '').toLowerCase().includes(q) ||
          (o.contact_email || '').toLowerCase().includes(q) ||
          (o.status || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [orders, statusFilter, search]);

  const paginatedOrders = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredOrders.slice(start, start + rowsPerPage);
  }, [filteredOrders, page, rowsPerPage]);

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelected(paginatedOrders.map((o) => o.id));
    else setSelected([]);
  };
  const handleSelectOne = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const isSelected = (id) => selected.includes(id);

  if (loading) return <AdminLoading />;
  if (error) return <Typography color="error" className="admin-error">{error}</Typography>;

  const statCards = [
    { label: 'Orders', value: stats.orders, icon: IoReceiptOutline, color: '#7367F0', bg: 'rgba(115, 103, 240, 0.12)', href: '/admin/orders' },
    { label: 'Customers', value: stats.customers, icon: IoPeopleOutline, color: '#00BAD1', bg: 'rgba(0, 186, 209, 0.12)', href: '/admin/customers' },
    { label: 'Products', value: stats.products, icon: IoShirtOutline, color: '#FF4C51', bg: 'rgba(255, 76, 81, 0.12)', href: '/admin/products' },
    { label: 'Revenue', value: formatPaise(stats.revenue), icon: IoPricetagsOutline, color: '#28C76F', bg: 'rgba(40, 199, 111, 0.12)', href: '/admin/orders' },
  ];

  return (
    <Box sx={{ minHeight: '100%' }}>
      {/* Hero section */}
      <Box
        sx={{
          position: 'relative',
          mb: 4,
          borderRadius: 3,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #7367F0 0%, #5E50D9 50%, #4A3EC7 100%)',
          boxShadow: '0 24px 48px -12px rgba(115, 103, 240, 0.4)',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.15) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.08) 0%, transparent 40%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Box sx={{ position: 'relative', p: { xs: 3, sm: 4, md: 5 } }}>
          <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.8)', letterSpacing: 2, fontWeight: 600 }}>
            Welcome back
          </Typography>
          <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700, mt: 0.5, letterSpacing: '-0.02em' }}>
            Rental performance overview
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mt: 1, maxWidth: 480 }}>
            Track your orders, revenue, and store activity at a glance.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 3 }}>
            <Typography variant="h3" sx={{ color: '#fff', fontWeight: 800, letterSpacing: '-0.03em' }}>
              {formatPaise(stats.revenue)}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
              total revenue
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', display: 'block', mt: 0.5 }}>
            From {stats.orders} orders
          </Typography>
          <Button
            variant="contained"
            sx={{
              mt: 3,
              px: 3,
              py: 1.5,
              bgcolor: 'rgba(255,255,255,0.95)',
              color: '#7367F0',
              fontWeight: 600,
              boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
              '&:hover': { bgcolor: '#fff', boxShadow: '0 6px 20px rgba(0,0,0,0.2)' },
            }}
            onClick={() => navigate('/admin/orders')}
          >
            View all orders
          </Button>
        </Box>
      </Box>

      {/* Festival countdown — top of dashboard */}
      <FestivalCountdownWidget />

      {/* 3 color cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              background: 'linear-gradient(135deg, #00BAD1 0%, #00A7BC 100%)',
              boxShadow: '0 8px 24px rgba(0, 186, 209, 0.35)',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(0, 186, 209, 0.4)' },
            }}
            onClick={() => navigate('/admin/sync')}
          >
            <CardContent sx={{ p: 2.5, color: '#fff' }}>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.9)', letterSpacing: 1.5, fontWeight: 600 }}>
                Drive sync
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                Sync products from Google Sheets
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', display: 'block', mt: 1 }}>
                Connect & import →
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              background: 'linear-gradient(135deg, #FF9F43 0%, #E68F3C 100%)',
              boxShadow: '0 8px 24px rgba(255, 159, 67, 0.35)',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(255, 159, 67, 0.4)' },
            }}
            onClick={() => navigate('/admin/products/add')}
          >
            <CardContent sx={{ p: 2.5, color: '#fff' }}>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.9)', letterSpacing: 1.5, fontWeight: 600 }}>
                Add product
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                Create a new rental product
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', display: 'block', mt: 1 }}>
                Quick add →
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
              boxShadow: '0 8px 24px rgba(156, 39, 176, 0.35)',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(156, 39, 176, 0.4)' },
            }}
            onClick={() => navigate('/admin/pages')}
          >
            <CardContent sx={{ p: 2.5, color: '#fff' }}>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.9)', letterSpacing: 1.5, fontWeight: 600 }}>
                Pages
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                Edit homepage & content blocks
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', display: 'block', mt: 1 }}>
                Manage pages →
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Stat cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map(({ label, value, icon: Icon, color, bg, href }) => (
          <Grid item xs={6} md={3} key={label}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                border: '1px solid rgba(0,0,0,0.04)',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
                },
              }}
              onClick={() => navigate(href)}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
                      {label}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: bg,
                    }}
                  >
                    <Icon size={24} style={{ color }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.04)' }}>
            <CardHeader
              title="Revenue & orders"
              subheader="By month (event date)"
              sx={{ pb: 0 }}
              subheaderTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
            />
            <CardContent sx={{ pt: 1 }}>
              <Box sx={{ width: '100%', height: 300, minHeight: 300, minWidth: 0 }}>
                <ResponsiveContainer width="100%" height={300} minWidth={0}>
                  <ComposedChart data={revenueByMonth} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#7367F0" stopOpacity={1} />
                        <stop offset="100%" stopColor="#7367F0" stopOpacity={0.6} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#6B7280' }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="left" tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={(v) => (v >= 100000 ? `₹${Math.round(v / 100000)}L` : `₹${Math.round(v / 1000)}k`)} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#6B7280' }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }}
                      formatter={(value, name) => [name === 'revenue' ? formatPaise(value) : value, name === 'revenue' ? 'Revenue' : 'Orders']}
                      labelFormatter={(l) => l}
                    />
                    <Bar yAxisId="left" dataKey="revenue" fill="url(#revenueGradient)" name="revenue" radius={[8, 8, 0, 0]} maxBarSize={40} />
                    <Line yAxisId="right" type="monotone" dataKey="orders" stroke="#00BAD1" name="orders" strokeWidth={2.5} dot={{ r: 5, fill: '#fff', stroke: '#00BAD1', strokeWidth: 2 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.04)' }}>
            <CardHeader title="Orders by status" sx={{ pb: 0 }} />
            <CardContent sx={{ pt: 1 }}>
              {ordersByStatus.length === 0 ? (
                <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary" variant="body2">No orders yet</Typography>
                </Box>
              ) : (
                <Box sx={{ width: '100%', height: 280, minHeight: 280, minWidth: 0 }}>
                  <ResponsiveContainer width="100%" height={280} minWidth={0}>
                    <PieChart>
                      <Pie
                        data={ordersByStatus}
                        cx="50%"
                        cy="50%"
                        innerRadius={70}
                        outerRadius={100}
                        paddingAngle={3}
                        dataKey="value"
                        nameKey="name"
                        stroke="none"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {ordersByStatus.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }} formatter={(value) => [value, 'Orders']} />
                      <Legend wrapperStyle={{ paddingTop: 8 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Popular products + Recent orders */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.04)' }}>
            <CardHeader
              title="Popular products"
              titleTypographyProps={{ fontWeight: 600 }}
              action={<Button size="small" variant="outlined" onClick={() => navigate('/admin/products')} sx={{ textTransform: 'none' }}>View all</Button>}
            />
            <CardContent sx={{ pt: 0 }}>
              {products.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary" variant="body2">No products yet</Typography>
                </Box>
              ) : (
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {products.slice(0, 5).map((p) => (
                    <Box
                      key={p.id}
                      component="li"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        py: 1.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      <Avatar variant="rounded" src={getImageUrl(p.first_image_url || p.first_image) || p.first_image_url || p.first_image} sx={{ width: 48, height: 48, borderRadius: 1.5 }}>{p.name ? p.name.charAt(0) : '?'}</Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={500} noWrap>{p.name}</Typography>
                        <Typography variant="caption" color="text.secondary">#{p.id}</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={600} color="primary.main">{p.min_price_paise != null ? formatPaise(p.min_price_paise) : '–'}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.04)' }}>
            <CardHeader
              title="Recent orders"
              subheader={`${orders.length} total`}
              subheaderTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
              action={<Button size="small" variant="outlined" onClick={() => navigate('/admin/orders')} sx={{ textTransform: 'none' }}>View all</Button>}
            />
            <CardContent sx={{ pt: 0 }}>
              {orders.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary" variant="body2">No orders yet</Typography>
                </Box>
              ) : (
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {orders.slice(0, 5).map((o) => (
                    <Box
                      key={o.id}
                      component="li"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        py: 1.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      <Chip label={(o.status || '').replace(/_/g, ' ')} size="small" color={ORDER_STATUS_COLORS_CHIP[o.status] || 'default'} variant="tonal" sx={{ fontWeight: 500 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={500}>#{o.id}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">{o.contact_name || o.contact_email || '–'}</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={600}>{formatPaise(o.total_paise)}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Transactions table */}
      <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.04)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Transactions
              </Typography>
              <Typography variant="caption" color="text.secondary">{filteredOrders.length} orders</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <TextField select size="small" value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }} sx={{ minWidth: 70 }} variant="outlined">
                <MenuItem value={6}>6</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </TextField>
              <TextField size="small" placeholder="Search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} sx={{ minWidth: { xs: 120, sm: 180 } }} variant="outlined" />
              <TextField select size="small" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} SelectProps={{ displayEmpty: true }} sx={{ minWidth: { xs: 120, sm: 140 } }} variant="outlined">
                <MenuItem value="">All status</MenuItem>
                <MenuItem value="pending_payment">Pending</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="out_for_delivery">Out for delivery</MenuItem>
                <MenuItem value="delivered">Delivered</MenuItem>
                <MenuItem value="returned">Returned</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>
              <Button variant="contained" size="small" onClick={() => navigate('/admin/orders')} sx={{ textTransform: 'none' }}>View all</Button>
            </Box>
          </Box>

          <TableContainer sx={{ overflowX: 'auto', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell padding="checkbox" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    <Checkbox indeterminate={selected.length > 0 && selected.length < paginatedOrders.length} checked={paginatedOrders.length > 0 && selected.length === paginatedOrders.length} onChange={handleSelectAll} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Event date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>No data available</TableCell>
                  </TableRow>
                ) : (
                  paginatedOrders.map((o) => (
                    <TableRow key={o.id} hover selected={isSelected(o.id)} sx={{ '&:hover': { bgcolor: 'action.hover' }, transition: 'background 0.15s' }}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={isSelected(o.id)} onChange={() => handleSelectOne(o.id)} />
                      </TableCell>
                      <TableCell>
                        <Typography component="span" variant="body2" sx={{ color: 'primary.main', cursor: 'pointer' }} onClick={() => navigate('/admin/orders')}>#{o.id}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={(o.status || '').replace(/_/g, ' ')} size="small" color={ORDER_STATUS_COLORS_CHIP[o.status] || 'default'} variant="tonal" />
                      </TableCell>
                      <TableCell>{formatPaise(o.total_paise)}</TableCell>
                      <TableCell>{o.event_date || '–'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => navigate('/admin/orders')} title="View"><IoEyeOutline size={18} /></IconButton>
                        <IconButton size="small" title="More"><IoEllipsisVertical size={18} /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredOrders.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            rowsPerPageOptions={[6, 10, 25, 50]}
            labelDisplayedRows={({ from, to, count }) => `Showing ${from} to ${to} of ${count} entries`}
            sx={{ borderTop: 1, borderColor: 'divider' }}
          />
        </CardContent>
      </Card>
    </Box>
  );
}
